import {
    BUILDING_SURVEY_CODE,
    formatMoney,
    NHG_APPENDIX_CODE,
    serviceLabelOf,
    type InspectionType,
} from '@/entities/order';

import { inspectionsCopy as copy } from './copy';

/**
 * Eén keuze bij "Dienst": de diensten die hij samen op de keuring zet.
 *
 * Bij het aanmaken kies je een keuring, geen losse regels. De NHG-bijlage is wel
 * een eigen dienst — met een eigen tarief, een eigen factuurregel en de bijlage in
 * het rapport — maar hij wordt bijna altijd samen met de bouwtechnische keuring
 * verkocht. Die twee staan daarom als één keuze in de lijst; wie ze los wil,
 * vindt ze achter "Andere samenstelling".
 */
export interface ServiceChoice {
    /** De waarde in de keuzelijst: de uuids van de diensten, aan elkaar. */
    key: string;
    label: string;
    /** De tweede regel onder de keuze; zegt wat deze samenstelling betekent. */
    hint?: string;
    /** De diensten die deze keuze aanzet, in de volgorde waarin ze op de keuring komen. */
    typeUuids: string[];
    /** Alleen te kiezen achter "Andere samenstelling". */
    separate: boolean;
}

/**
 * De keuzes bij "Dienst", uit de diensten die dit bedrijf levert.
 *
 * Levert het bedrijf zowel de bouwtechnische keuring als de NHG-bijlage, dan komt
 * de samengestelde keuze bovenaan en staan die twee diensten ook nog los in de
 * lijst, achter "Andere samenstelling". Ontbreekt de NHG-bijlage in de
 * dienstenlijst, dan valt er niets samen te stellen: dan is er gewoon één keuze
 * "Bouwtechnische keuring", net als voorheen.
 *
 * Elke andere dienst (een energielabel, een conditiemeting) blijft zijn eigen
 * keuze, in de volgorde waarin het bedrijf ze verkoopt.
 */
export function serviceChoices(types: readonly InspectionType[]): ServiceChoice[] {
    const survey = types.find((type) => type.code === BUILDING_SURVEY_CODE);
    const appendix = types.find((type) => type.code === NHG_APPENDIX_CODE);
    const combines = survey !== undefined && appendix !== undefined;
    const choices: ServiceChoice[] = [];

    if (survey && appendix) {
        choices.push({
            key: `${survey.uuid}+${appendix.uuid}`,
            label: serviceLabelOf([survey, appendix]),
            hint: togetherHint(appendix),
            typeUuids: [survey.uuid, appendix.uuid],
            separate: false,
        });
    }

    for (const type of types) {
        const separate = combines && (type === survey || type === appendix);

        choices.push({
            key: type.uuid,
            label: type.name,
            hint: separate ? hintFor(type, survey, appendix) : undefined,
            typeUuids: [type.uuid],
            separate,
        });
    }

    return choices;
}

/**
 * Wat de samengestelde keuze de klant kost.
 *
 * Staat er een tarief voor de bijlage, dan komt hij als eigen regel op de factuur —
 * met het bedrag erbij als je de tarieven van je bedrijf mag zien. Staat er geen
 * tarief, dan is de bijlage inbegrepen bij de prijs van de keuring en komt hij
 * helemaal niet op de factuur.
 */
function togetherHint(appendix: InspectionType): string {
    if (!appendix.billedSeparately) {
        return copy.newServiceIncludedHint(appendix.name);
    }

    return appendix.price === null
        ? copy.newServiceTogetherHint
        : copy.newServicePricedHint(formatMoney(appendix.price));
}

/** Waarom je deze dienst los zou kiezen: zonder de bijlage, of alleen de bijlage. */
function hintFor(
    type: InspectionType,
    survey: InspectionType | undefined,
    appendix: InspectionType | undefined,
): string | undefined {
    if (type === survey && appendix) {
        return copy.newServiceWithoutAppendix(appendix.name);
    }

    return type === appendix ? copy.newServiceAppendixOnly : undefined;
}
